import { useState, useEffect, useMemo } from 'react';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingScreen';
import { AppIcon } from '@/components/AppIcon';
import { CategoryMark } from '@/components/CategoryMark';

interface PendingItem {
  paymentId: string;
  description: string;
  amount: number;
  categoryId: string;
  categoryName: string;
  categoryIcon: string;
}

interface PendingGroup {
  paidByUserId: string;
  paidByUserName: string;
  items: PendingItem[];
  subtotal: number;
}

interface SettlementSummary {
  yearMonth: string;
  userPayments: Array<{
    userId: string;
    userName: string;
    totalPaid: number;
  }>;
  pendingItems: PendingGroup[];
  settlement: {
    fromUserId: string;
    fromUserName: string;
    toUserId: string;
    toUserName: string;
    amount: number;
  } | null;
}

interface SettlementHistory {
  id: string;
  name?: string;
  total_amount: number;
  from_user_name: string;
  to_user_name: string;
  settled_at: string;
  memo?: string;
}

// カテゴリごとにグループ化した型
interface CategoryGroup {
  categoryId: string;
  categoryName: string;
  categoryIcon: string;
  items: Array<PendingItem & { paidByUserId: string; paidByUserName: string }>;
  subtotal: number;
}

export default function SettlementsPage() {
  const { user } = useAuthStore();
  const { currentYearMonth, showToast } = useAppStore();
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState<'current' | 'history'>('current');
  const [summary, setSummary] = useState<SettlementSummary | null>(null);
  const [history, setHistory] = useState<SettlementHistory[]>([]);
  const [isSettling, setIsSettling] = useState(false);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<Set<string>>(new Set());
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [settlementName, setSettlementName] = useState('');
  const [expandedSettlementId, setExpandedSettlementId] = useState<string | null>(null);
  const [settlementDetails, setSettlementDetails] = useState<Record<string, any[]>>({});
  const [includeImmediateSettlements, setIncludeImmediateSettlements] = useState(false);
  const [editingSettlementId, setEditingSettlementId] = useState<string | null>(null);
  const [editingSettlementName, setEditingSettlementName] = useState('');
  const [isRenamingSettlement, setIsRenamingSettlement] = useState(false);

  useEffect(() => {
    const date = new Date();
    setSettlementName(`${date.getMonth() + 1}月分`);
  }, []);

  useEffect(() => {
    loadData();
  }, [currentYearMonth]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [summaryRes, historyRes] = await Promise.all([
        api.getSettlementSummary(currentYearMonth),
        api.getSettlementHistory(),
      ]);
      setSummary(summaryRes);
      setHistory(historyRes.settlements || []);
    } catch (error) {
      console.error('Failed to load settlements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 全ての未精算アイテムをフラット化
  const allPendingItems = useMemo(() => {
    if (!summary) return [];
    return summary.pendingItems.flatMap(group =>
      group.items.map(item => ({
        ...item,
        paidByUserId: group.paidByUserId,
        paidByUserName: group.paidByUserName,
      }))
    );
  }, [summary]);

  // カテゴリ別にグループ化
  const categoryGroups = useMemo((): CategoryGroup[] => {
    const groups = new Map<string, CategoryGroup>();
    for (const item of allPendingItems) {
      const key = item.categoryId || 'unknown';
      if (!groups.has(key)) {
        groups.set(key, {
          categoryId: item.categoryId,
          categoryName: item.categoryName || '未分類',
          categoryIcon: item.categoryIcon || '',
          items: [],
          subtotal: 0,
        });
      }
      const group = groups.get(key)!;
      group.items.push(item);
      group.subtotal += item.amount;
    }
    return Array.from(groups.values());
  }, [allPendingItems]);

  // フィルタ後のアイテム
  const filteredItems = useMemo(() => {
    if (!selectedCategoryId) return allPendingItems;
    return allPendingItems.filter(item => item.categoryId === selectedCategoryId);
  }, [allPendingItems, selectedCategoryId]);

  // 精算対象の合計金額を計算
  const getSettlementAmount = (): number => {
    return filteredItems
      .filter(item => selectedPaymentIds.has(item.paymentId))
      .reduce((sum, item) => sum + item.amount, 0);
  };

  const handleSettle = async () => {
    const paymentIds = Array.from(selectedPaymentIds);
    if (paymentIds.length === 0) {
      showToast('精算する項目を選択してください', 'error');
      return;
    }

    const amount = getSettlementAmount();
    const count = paymentIds.length;

    if (!confirm(`${count}件（¥${amount.toLocaleString()}）を精算しますか？`)) return;

    setIsSettling(true);
    try {
      await api.createSettlement({ paymentIds, name: settlementName });
      showToast('精算が完了しました', 'success');
      setSelectedPaymentIds(new Set());
      // 名前をリセット（次の月へ）
      const date = new Date();
      setSettlementName(`${date.getMonth() + 1}月分`);
      loadData();
    } catch (error: any) {
      showToast(error.message || '精算に失敗しました', 'error');
    } finally {
      setIsSettling(false);
    }
  };

  const toggleHistoryDetails = async (id: string) => {
    if (expandedSettlementId === id) {
      setExpandedSettlementId(null);
      return;
    }

    setExpandedSettlementId(id);
    if (!settlementDetails[id]) {
      try {
        const res = await api.getSettlementDetails(id);
        setSettlementDetails(prev => ({ ...prev, [id]: res.payments }));
      } catch (error) {
        console.error('Failed to load details:', error);
      }
    }
  };

  const startEditingSettlement = (settlement: SettlementHistory) => {
    setEditingSettlementId(settlement.id);
    setEditingSettlementName(settlement.name || '');
  };

  const cancelEditingSettlement = () => {
    setEditingSettlementId(null);
    setEditingSettlementName('');
  };

  const saveSettlementName = async (settlementId: string) => {
    const name = editingSettlementName.trim();
    if (!name) {
      showToast('精算名を入力してください', 'error');
      return;
    }

    setIsRenamingSettlement(true);
    try {
      const response = await api.updateSettlementName(settlementId, name);
      setHistory(prev => prev.map(settlement =>
        settlement.id === settlementId ? { ...settlement, name: response.name } : settlement
      ));
      cancelEditingSettlement();
      showToast('精算名を変更しました', 'success');
    } catch (error: any) {
      showToast(error.message || '精算名の変更に失敗しました', 'error');
    } finally {
      setIsRenamingSettlement(false);
    }
  };

  // 個別選択のトグル
  const togglePaymentSelection = (paymentId: string) => {
    setSelectedPaymentIds(prev => {
      const next = new Set(prev);
      if (next.has(paymentId)) {
        next.delete(paymentId);
      } else {
        next.add(paymentId);
      }
      return next;
    });
  };

  // 一括選択（全選択/全解除） - フィルタ後のアイテムに対して
  const selectAll = () => {
    setSelectedPaymentIds(new Set(filteredItems.map(item => item.paymentId)));
  };

  const deselectAll = () => {
    setSelectedPaymentIds(new Set());
  };


  // 自分にとっての精算状況を計算
  const getMySettlementInfo = () => {
    if (!summary?.settlement || !user) {
      return { direction: 'even' as const, amount: 0, partnerName: '' };
    }

    const { settlement } = summary;
    const isIPay = settlement.fromUserId === user.id;
    const partnerName = isIPay ? settlement.toUserName : settlement.fromUserName;

    return {
      direction: isIPay ? 'pay' as const : 'receive' as const,
      amount: settlement.amount,
      partnerName,
    };
  };

  // 各ユーザーの負担額を計算
  const getUserTotals = () => {
    if (!summary || !user) return { myTotal: 0, partnerTotal: 0, partnerName: '' };

    const myPayment = summary.userPayments.find(p => p.userId === user.id);
    const partnerPayment = summary.userPayments.find(p => p.userId !== user.id);

    return {
      myTotal: myPayment?.totalPaid || 0,
      partnerTotal: partnerPayment?.totalPaid || 0,
      partnerName: partnerPayment?.userName || 'パートナー',
    };
  };

  const formatCurrency = (amount: number) => {
    return `¥${amount.toLocaleString()}`;
  };

  const settlementInfo = getMySettlementInfo();
  const userTotals = getUserTotals();
  const hasPendingItems = allPendingItems.length > 0;
  const settlementAmount = getSettlementAmount();
  const selectedInFilterCount = filteredItems.filter(item => selectedPaymentIds.has(item.paymentId)).length;
  const isAllFilteredSelected = selectedInFilterCount === filteredItems.length && filteredItems.length > 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pb-4">
      <div className="page-topbar">
        <h1 className="text-lg font-bold text-center py-4">精算</h1>
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setTab('current')}
            className={`flex-1 py-3 text-center font-medium ${tab === 'current'
              ? 'text-primary-500 border-b-2 border-primary-500'
              : 'text-gray-500'
              }`}
          >
            未精算
          </button>
          <button
            onClick={() => setTab('history')}
            className={`flex-1 py-3 text-center font-medium ${tab === 'history'
              ? 'text-primary-500 border-b-2 border-primary-500'
              : 'text-gray-500'
              }`}
          >
            履歴
          </button>
        </div>
      </div>

      {tab === 'current' ? (
        <div className="p-4">
          {hasPendingItems ? (
            <>
              {/* 精算サマリー */}
              <div className="card p-6 text-center mb-4">
                <p className="text-sm text-gray-500 mb-2">
                  {settlementInfo.direction === 'pay'
                    ? `${settlementInfo.partnerName}に支払い`
                    : settlementInfo.direction === 'receive'
                      ? `${settlementInfo.partnerName}から受取`
                      : '精算なし'}
                </p>
                <p
                  className={`text-4xl font-bold ${settlementInfo.direction === 'pay'
                    ? 'text-red-500'
                    : settlementInfo.direction === 'receive'
                      ? 'text-green-500'
                      : 'text-gray-500'
                    }`}
                >
                  {formatCurrency(Math.abs(settlementInfo.amount))}
                </p>

                <div className="flex justify-around mt-6 text-sm">
                  <div>
                    <p className="text-gray-500">自分の支払い</p>
                    <p className="font-semibold">{formatCurrency(userTotals.myTotal)}</p>
                  </div>
                  <div className="w-px bg-gray-200" />
                  <div>
                    <p className="text-gray-500">{userTotals.partnerName}の支払い</p>
                    <p className="font-semibold">{formatCurrency(userTotals.partnerTotal)}</p>
                  </div>
                </div>
              </div>

              {/* カテゴリフィルター */}
              <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
                <button
                  onClick={() => setSelectedCategoryId(null)}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${!selectedCategoryId
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700'
                    }`}
                >
                  すべて ({allPendingItems.length})
                </button>
                {categoryGroups.map((group) => (
                  <button
                    key={group.categoryId}
                    onClick={() => setSelectedCategoryId(group.categoryId)}
                    className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap flex items-center gap-1 ${selectedCategoryId === group.categoryId
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700'
                      }`}
                  >
                    <span>{group.categoryName}</span>
                    <span className="text-xs opacity-70">({group.items.length})</span>
                  </button>
                ))}
              </div>

              {/* 精算ボタン */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">精算名</label>
                <input
                  type="text"
                  value={settlementName}
                  onChange={(e) => setSettlementName(e.target.value)}
                  className="input"
                  placeholder="例: 11月分"
                />
              </div>
              <button
                onClick={handleSettle}
                disabled={isSettling || selectedPaymentIds.size === 0}
                className="btn btn-primary btn-lg w-full mb-4 disabled:opacity-50"
              >
                {isSettling
                  ? '処理中...'
                  : selectedPaymentIds.size > 0
                    ? `${selectedPaymentIds.size}件（${formatCurrency(settlementAmount)}）を精算する`
                    : '精算する項目を選択してください'
                }
              </button>

              {/* 支出リスト */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-medium">
                    {selectedCategoryId
                      ? `${categoryGroups.find(g => g.categoryId === selectedCategoryId)?.categoryName || ''} (${selectedInFilterCount}/${filteredItems.length}件選択中)`
                      : `未精算の支出 (${selectedPaymentIds.size}/${allPendingItems.length}件選択中)`
                    }
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={isAllFilteredSelected ? deselectAll : selectAll}
                      className="btn btn-sm btn-outline"
                    >
                      {isAllFilteredSelected ? '選択解除' : '一括選択'}
                    </button>
                  </div>
                </div>
                <div className="card divide-y divide-gray-100">
                  {filteredItems.map((item) => (
                    <div
                      key={item.paymentId}
                      onClick={() => togglePaymentSelection(item.paymentId)}
                      className={`p-4 cursor-pointer transition-colors ${selectedPaymentIds.has(item.paymentId) ? 'bg-primary-50' : ''
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedPaymentIds.has(item.paymentId)
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-gray-300'
                          }`}>
                          {selectedPaymentIds.has(item.paymentId) && (
                            <span className="text-white text-xs">✓</span>
                          )}
                        </div>
                        <CategoryMark name={item.categoryName} size="sm" />
                        <div className="flex-1">
                          <p className="font-medium">{item.description}</p>
                          <p className="text-sm text-gray-500">
                            {item.categoryName || '未分類'} • {item.paidByUserName}が立替
                          </p>
                        </div>
                        <p className="font-semibold">{formatCurrency(item.amount)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="empty-icon mx-auto mb-3"><AppIcon name="spark" size={22} /></div>
              <p className="text-gray-500">未精算の支出はありません</p>
            </div>
          )}
        </div>
      ) : (
        <div className="p-4">
          {/* 即時清算フィルター */}
          <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="includeImmediate"
              checked={includeImmediateSettlements}
              onChange={(e) => setIncludeImmediateSettlements(e.target.checked)}
              className="w-4 h-4 text-primary-500 rounded"
            />
            <label htmlFor="includeImmediate" className="text-sm text-gray-700 cursor-pointer">
              即時清算を含む
            </label>
          </div>
          {(() => {
            const filteredHistory = includeImmediateSettlements
              ? history
              : history.filter(s => !s.name?.includes('即時清算'));
            return filteredHistory.length > 0 ? (
              <div className="card divide-y divide-gray-100">
                {filteredHistory.map((settlement) => (
                  <div key={settlement.id} className="p-4">
                    <div
                      className="flex justify-between items-start mb-1 cursor-pointer"
                      onClick={() => toggleHistoryDetails(settlement.id)}
                    >
                      <div className="min-w-0 flex-1">
                        {editingSettlementId === settlement.id ? (
                          <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
                            <input
                              type="text"
                              value={editingSettlementName}
                              onChange={(event) => setEditingSettlementName(event.target.value)}
                              className="input min-w-0 flex-1"
                              aria-label="精算名"
                              maxLength={50}
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => saveSettlementName(settlement.id)}
                              className="btn btn-primary btn-sm"
                              disabled={isRenamingSettlement}
                            >
                              保存
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditingSettlement}
                              className="btn btn-ghost btn-sm"
                              disabled={isRenamingSettlement}
                            >
                              キャンセル
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-lg truncate">{settlement.name || '名称未設定'}</p>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                startEditingSettlement(settlement);
                              }}
                              className="icon-button !w-8 !h-8 !rounded-lg !shadow-none"
                              aria-label={`${settlement.name || '名称未設定'}を編集`}
                            >
                              <AppIcon name="edit" size={14} />
                            </button>
                          </div>
                        )}
                        <p className="font-medium text-sm text-gray-600">
                          {settlement.from_user_name} → {settlement.to_user_name}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">
                          {formatCurrency(settlement.total_amount)}
                        </p>
                        <p className="text-xs text-primary-500 mt-1 inline-flex items-center gap-1">
                          <AppIcon name={expandedSettlementId === settlement.id ? 'chevronUp' : 'chevronDown'} size={13} />
                          {expandedSettlementId === settlement.id ? '閉じる' : '詳細'}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">
                      {new Date(settlement.settled_at).toLocaleDateString('ja-JP')}
                    </p>
                    {settlement.memo && (
                      <p className="text-sm text-gray-400 mt-1">{settlement.memo}</p>
                    )}

                    {/* 詳細表示 */}
                    {expandedSettlementId === settlement.id && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        {!settlementDetails[settlement.id] ? (
                          <div className="flex justify-center py-2">
                            <LoadingSpinner size="sm" />
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {settlementDetails[settlement.id].map((payment) => (
                              <div key={payment.id} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <CategoryMark name={payment.category_name} size="sm" />
                                  <span>{payment.description}</span>
                                </div>
                                <span>{formatCurrency(payment.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="empty-icon mx-auto mb-3"><AppIcon name="history" size={22} /></div>
                <p className="text-gray-500">精算履歴がありません</p>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
